import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { 
  Assessment as AssessmentIcon, 
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon
} from '@mui/icons-material';
import api from '../utils/api';

interface Document {
  id: number;
  title: string;
  description: string;
  version: string;
  is_current_version: boolean;
}

interface TrackingData {
  document: {
    id: number;
    title: string;
    version: string;
    created_at: string;
  };
  summary: {
    total_assigned: number;
    total_acknowledged: number;
    total_pending: number;
    total_overdue: number;
    acknowledgment_rate: number;
  };
  assignments: Array<{
    id: number;
    assigned_user: {
      id: number;
      email: string;
      department: string;
    };
    assigned_by: {
      id: number;
      email: string;
    };
    assigned_at: string;
    due_date: string;
    status: string;
    acknowledgment: {
      status: string;
      acknowledged_at: string;
      times_viewed: number;
      first_viewed_at: string;
      last_viewed_at: string;
      total_view_time: string;
      user_notes: string;
    } | null;
  }>;
}

interface DocumentTrackingReportDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document | null;
}

const DocumentTrackingReportDialog: React.FC<DocumentTrackingReportDialogProps> = ({
  open,
  onClose,
  document: documentData
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);

  useEffect(() => {
    if (open && documentData) {
      fetchTrackingReport();
    }
  }, [open, documentData]);

  const fetchTrackingReport = async () => {
    if (!documentData) return;

    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/documents/${documentData.id}/tracking_report/`);
      setTrackingData(response.data);

    } catch (error: any) {
      console.error('Error fetching tracking report:', error);
      if (error.response?.data) {
        setError(`Failed to load tracking report: ${error.response.data}`);
      } else {
        setError('Failed to load tracking report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    if (!documentData) return;
    
    try {
      const response = await api.get(`/documents/${documentData.id}/tracking_report_csv/`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${documentData.title}_tracking_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    }
  };

  const handleDownloadPDF = async () => {
    if (!documentData) return;
    
    try {
      const response = await api.get(`/documents/${documentData.id}/tracking_report_pdf/`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${documentData.title}_tracking_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'acknowledged':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!documentData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                         <Typography variant="h6">
               Tracking Report: {documentData.title}
             </Typography>
          </Box>
          <Box>
            <Button
              startIcon={<CsvIcon />}
              onClick={handleDownloadCSV}
              size="small"
              sx={{ mr: 1 }}
            >
              CSV
            </Button>
            <Button
              startIcon={<PdfIcon />}
              onClick={handleDownloadPDF}
              size="small"
            >
              PDF
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
                 ) : trackingData && (trackingData.summary || trackingData.assignments) ? (
           <Box>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Assigned
                    </Typography>
                                         <Typography variant="h4">
                       {trackingData.summary?.total_assigned || 0}
                     </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Acknowledged
                    </Typography>
                                         <Typography variant="h4" color="success.main">
                       {trackingData.summary?.total_acknowledged || 0}
                     </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Pending
                    </Typography>
                                         <Typography variant="h4" color="warning.main">
                       {trackingData.summary?.total_pending || 0}
                     </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Overdue
                    </Typography>
                                         <Typography variant="h4" color="error.main">
                       {trackingData.summary?.total_overdue || 0}
                     </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
                             <Typography variant="h6" gutterBottom>
                 Acknowledgment Rate: {trackingData.summary?.acknowledgment_rate || 0}%
               </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Assignments Table */}
            <Typography variant="h6" gutterBottom>
              Assignment Details
            </Typography>
            
                         <TableContainer component={Paper}>
               <Table>
                 <TableHead>
                   <TableRow>
                     <TableCell>User</TableCell>
                     <TableCell>Department</TableCell>
                     <TableCell>Assigned By</TableCell>
                     <TableCell>Assigned Date</TableCell>
                     <TableCell>Due Date</TableCell>
                     <TableCell>Status</TableCell>
                     <TableCell>Times Viewed</TableCell>
                     <TableCell>Last Viewed</TableCell>
                   </TableRow>
                 </TableHead>
                 <TableBody>
                   {trackingData.assignments && trackingData.assignments.length > 0 ? (
                     trackingData.assignments.map((assignment) => (
                    <TableRow key={assignment.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {assignment.assigned_user.email}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {assignment.assigned_user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{assignment.assigned_user.department}</TableCell>
                      <TableCell>{assignment.assigned_by.email}</TableCell>
                      <TableCell>{formatDate(assignment.assigned_at)}</TableCell>
                      <TableCell>{assignment.due_date ? formatDate(assignment.due_date) : 'No due date'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={assignment.acknowledgment?.status || assignment.status} 
                          color={getStatusColor(assignment.acknowledgment?.status || assignment.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{assignment.acknowledgment?.times_viewed || 0}</TableCell>
                      <TableCell>
                        {assignment.acknowledgment?.last_viewed_at 
                          ? formatDateTime(assignment.acknowledgment.last_viewed_at)
                          : 'Never viewed'
                        }
                                             </TableCell>
                     </TableRow>
                   ))
                   ) : (
                     <TableRow>
                       <TableCell colSpan={8} align="center">
                         <Typography variant="body2" color="textSecondary">
                           No assignments found for this document.
                         </Typography>
                       </TableCell>
                     </TableRow>
                   )}
                 </TableBody>
               </Table>
                          </TableContainer>
           </Box>
         ) : !loading && (
           <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
             <Typography variant="body1" color="textSecondary">
               No tracking data available for this document.
             </Typography>
           </Box>
         )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentTrackingReportDialog; 